import { z } from "zod";

/**
 * URL search params for `/$workspaceSlug/payroll/my-details`.
 *
 * `section` is which card to scroll to and open, so a readiness item, a
 * notification and a link from My payslips can all land on the thing they are
 * about rather than at the top of a long page. `request` names one change
 * request to focus, which is how "your change was rejected" gets somebody back
 * to the reason without hunting.
 *
 * Neither is a value. No account number, no PAN, no amount ever becomes a
 * search param: the URL is the one place a figure ends up in browser history,
 * a referrer header and somebody else's screen share (FE-SEC7). Both of these
 * are opaque identifiers or a fixed vocabulary.
 */
export const PAY_DETAILS_SECTIONS = [
  "bank",
  "statutory",
  "legal_name",
  "regime",
  "tax",
  "documents",
] as const;
export type PayDetailsSection = (typeof PAY_DETAILS_SECTIONS)[number];

export const myPayDetailsRouteSearchSchema = z
  .object({
    /// Plain `z.string()` rather than an enum, for the reason
    /// `my-payslips.route.search.ts` gives at length: `.catch` would rewrite
    /// the parsed value while leaving the URL saying something else, so the
    /// address bar and the page would disagree for the rest of the session.
    section: z.string().optional(),
    request: z.string().optional(),
    /// Which tax year the money section below is about (D10). In the URL
    /// rather than component state (S3) so "the year I am asking about" is a
    /// link somebody can send to their payroll team. A period id is opaque and
    /// carries nothing about the person or the money (FE-SEC7).
    taxPeriod: z.string().optional(),
  })
  .strict();

export type MyPayDetailsRouteSearch = z.infer<typeof myPayDetailsRouteSearchSchema>;

/// The section to open. An unknown value opens none, which is the same as
/// arriving without one: a stale bookmark should show the page, not an error.
export function resolvePayDetailsSection(
  section: string | undefined,
): PayDetailsSection | undefined {
  if (section === undefined) return undefined;
  return PAY_DETAILS_SECTIONS.includes(section as PayDetailsSection)
    ? (section as PayDetailsSection)
    : undefined;
}
